import { Link } from "react-router-dom";
import { SERVICES, CITIES, CONTACT, PARTNERS } from "@/data/site";
import Logo from "@/components/Logo";
import { track } from "@/lib/track";
import { ArrowUpRight, Mail, Phone, MapPin, Clock } from "lucide-react";

export default function Footer() {
  return (
    <footer className="relative z-10 border-t border-ink/10 bg-mist" data-testid="site-footer">
      {/* Availability banner */}
      <div className="flex flex-col items-center justify-center gap-2 border-b border-ink/10 bg-ink px-5 py-4 text-center text-white md:flex-row md:gap-6">
        <span className="flex items-center gap-2 text-sm"><Clock size={15} className="text-lime" /> {CONTACT.availability}</span>
        <span className="hidden text-white/30 md:inline">|</span>
        <span className="flex items-center gap-3 text-sm">
          <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className="hover:text-lime">{CONTACT.phoneMobile}</a>
          <span className="text-white/30">·</span>
          <a href={`tel:${CONTACT.phoneFixe.replace(/\s/g, "")}`} className="hover:text-lime">{CONTACT.phoneFixe}</a>
        </span>
      </div>

      {/* Partner brands strip — all 5 sister brands, one row */}
      <div className="border-b border-ink/10 bg-white px-5 py-8 md:px-10">
        <p className="text-center text-[11px] uppercase tracking-[0.28em] text-ink/40">Notre réseau de marques partenaires</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {PARTNERS.map((p) => (
            <a
              key={p.domain}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col rounded-xl border border-ink/10 bg-mist px-4 py-4 transition-all hover:border-lime-dim/60 hover:-translate-y-0.5"
              data-testid={`footer-partner-${p.domain.replace(/\./g, "-")}`}
            >
              <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45">{p.category}</span>
              <span className="mt-1 font-serif text-lg text-ink">{p.name}</span>
              <span className="mt-2 flex items-center gap-1 text-xs text-lime-dim">{p.domain}<ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
            </a>
          ))}
        </div>
      </div>

      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-10">
        <div className="md:col-span-4">
          <Logo size="md" />
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-ink/60">
            Agence événementielle B2B. Nous concevons et produisons des événements d'entreprise qui font vendre et marquent les esprits.
          </p>
          <div className="mt-6 space-y-2 text-sm text-ink/70">
            <p className="flex items-start gap-2"><MapPin size={15} className="mt-0.5 shrink-0 text-lime-dim" /> <span>{CONTACT.address}<br /><span className="text-ink/45">{CONTACT.building}</span></span></p>
            <p className="text-xs text-ink/45">{CONTACT.legalName} · SIRET {CONTACT.siret}</p>
            <a href={`mailto:${CONTACT.email}`} onClick={() => track("click_email")} className="flex items-center gap-2 hover:text-lime-dim"><Mail size={15} /> {CONTACT.email}</a>
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className="flex items-center gap-2 hover:text-lime-dim"><Phone size={15} /> {CONTACT.phoneMobile} <span className="text-ink/45">· {CONTACT.phoneFixe}</span></a>
          </div>
        </div>

        <div className="md:col-span-3 md:col-start-6">
          <h4 className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Prestations</h4>
          <ul className="mt-5 space-y-3">
            {SERVICES.slice(0, 6).map((s) => (<li key={s.slug}><Link to={`/prestations/${s.slug}`} className="text-sm text-ink/70 hover:text-lime-dim">{s.title}</Link></li>))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Villes</h4>
          <ul className="mt-5 space-y-3">
            {CITIES.map((c) => (<li key={c.slug}><Link to={c.slug === "lyon" ? "/agence-evenementielle-entreprise-lyon" : `/villes/${c.slug}`} className="text-sm text-ink/70 hover:text-lime-dim">{c.name}</Link></li>))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Expertises locales</h4>
          <ul className="mt-5 space-y-3">
            <li><Link to="/agence-evenementielle-entreprise-lyon" className="text-sm text-ink/70 hover:text-lime-dim">Agence Lyon</Link></li>
            <li><Link to="/agence-evenementielle-grenoble" className="text-sm text-ink/70 hover:text-lime-dim">Agence Grenoble</Link></li>
            <li><Link to="/agence-evenementielle-valence" className="text-sm text-ink/70 hover:text-lime-dim">Agence Valence</Link></li>
            <li><Link to="/soiree-entreprise-lyon" className="text-sm text-ink/70 hover:text-lime-dim">Soirée d'entreprise Lyon</Link></li>
            <li><Link to="/seminaire-convention-lyon" className="text-sm text-ink/70 hover:text-lime-dim">Séminaire Lyon</Link></li>
            <li><Link to="/inauguration-entreprise-lyon" className="text-sm text-ink/70 hover:text-lime-dim">Inauguration Lyon</Link></li>
            <li><Link to="/lancement-produit-lyon" className="text-sm text-ink/70 hover:text-lime-dim">Lancement produit Lyon</Link></li>
            <li><Link to="/salon-professionnel-lyon" className="text-sm text-ink/70 hover:text-lime-dim">Salon professionnel Lyon</Link></li>
            <li><Link to="/soiree-entreprise-paris" className="text-sm text-ink/70 hover:text-lime-dim">Soirée d'entreprise Paris</Link></li>
            <li><Link to="/lancement-produit-paris" className="text-sm text-ink/70 hover:text-lime-dim">Lancement produit Paris</Link></li>
            <li><Link to="/inauguration-entreprise-paris" className="text-sm text-ink/70 hover:text-lime-dim">Inauguration Paris</Link></li>
            <li><Link to="/agence-evenementielle-saint-etienne" className="text-sm text-ink/70 hover:text-lime-dim">Agence Saint-Étienne</Link></li>
            <li><Link to="/agence-evenementielle-clermont-ferrand" className="text-sm text-ink/70 hover:text-lime-dim">Agence Clermont-Ferrand</Link></li>
            <li><Link to="/agence-evenementielle-chambery-annecy" className="text-sm text-ink/70 hover:text-lime-dim">Agence Chambéry-Annecy</Link></li>
            <li><Link to="/agence-evenementielle-bourg-en-bresse" className="text-sm text-ink/70 hover:text-lime-dim">Agence Bourg-en-Bresse</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Agence</h4>
          <ul className="mt-5 space-y-3">
            <li><Link to="/a-propos" className="text-sm text-ink/70 hover:text-lime-dim">À propos</Link></li>
            <li><Link to="/blog" className="text-sm text-ink/70 hover:text-lime-dim">Blog</Link></li>
            <li><Link to="/contact" className="text-sm text-ink/70 hover:text-lime-dim">Contact</Link></li>
            <li><Link to="/rejoignez-nous" className="text-sm text-ink/70 hover:text-lime-dim">Rejoignez-nous</Link></li>
            <li><Link to="/devis" className="text-sm text-ink/70 hover:text-lime-dim">Demande de devis</Link></li>
          </ul>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-3 border-t border-ink/10 px-5 py-6 text-xs text-ink/40 md:flex-row md:items-center md:px-10">
        <p>© {new Date().getFullYear()} {CONTACT.legalName} — {CONTACT.brand}. SIRET {CONTACT.siret}. {CONTACT.zones}</p>
        <div className="flex items-center gap-4">
          <Link to="/mentions-legales" className="hover:text-lime-dim" data-testid="footer-legal-link">Mentions légales & CGV</Link>
          <span className="hidden font-serif text-base italic text-ink/50 md:inline">Chaque événement est un <span className="not-italic text-lime-dim">désir</span> réalisé.</span>
        </div>
      </div>
    </footer>
  );
}
